import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, UsersThree, PencilSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", filiere: "", promotion: "" });
  const [membersOpen, setMembersOpen] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => {
    api.get("/groups").then((r) => setGroups(r.data));
    api.get("/users?role=etudiant").then((r) => setStudents(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/groups", form); toast.success("Groupe créé"); setOpen(false); setForm({ name: "", description: "", filiere: "", promotion: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openMembers = (g) => { setSelected(g.members.map((m) => m.id)); setMembersOpen(g); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const saveMembers = async () => {
    try { await api.post(`/groups/${membersOpen.id}/members`, { student_ids: selected }); toast.success("Membres mis à jour"); setMembersOpen(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/groups/${id}`); toast.success("Groupe supprimé"); load(); };

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Groupes</h1>
          <p className="text-sm text-muted-foreground">Créez des groupes et affectez-y des étudiants. Les professeurs donnent ensuite accès à leurs cours aux groupes.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-group-btn"><Plus size={18} className="mr-2" /> Nouveau groupe</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Créer un groupe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="flex gap-3">
                <div className="flex-1"><Label>Filière</Label><Input value={form.filiere} onChange={(e) => setForm({ ...form, filiere: e.target.value })} /></div>
                <div className="flex-1"><Label>Promotion</Label><Input value={form.promotion} onChange={(e) => setForm({ ...form, promotion: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="save-group-btn">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {groups.length === 0 && <p className="text-sm text-muted-foreground">Aucun groupe. Créez-en un pour commencer.</p>}
        {groups.map((g) => (
          <div key={g.id} className="rounded-xl border border-border bg-card p-5" data-testid={`group-card-${g.id}`}>
            <div className="mb-2 flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><UsersThree size={22} weight="duotone" /></div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openMembers(g)} data-testid={`members-${g.id}`}><PencilSimple size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(g.id)}><Trash size={16} className="text-accent" /></Button>
              </div>
            </div>
            <h3 className="font-display text-lg font-bold">{g.name}</h3>
            <p className="text-sm text-muted-foreground">{g.description}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              <Badge variant="secondary">{g.member_count} étudiant(s)</Badge>
              {g.filiere && <Badge variant="secondary">{g.filiere}</Badge>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!membersOpen} onOpenChange={(o) => !o && setMembersOpen(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Membres — {membersOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {students.length === 0 && <p className="text-sm text-muted-foreground">Aucun étudiant. Créez des comptes étudiants d'abord.</p>}
            {students.map((s) => (
              <label key={s.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm" data-testid={`member-opt-${s.id}`}>
                <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                <span className="font-medium">{s.name}</span>
                <span className="text-muted-foreground">{s.email}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={saveMembers} className="bg-primary hover:bg-primary/90" data-testid="save-members-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
